import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { neutral, blue } from '../../../theme/colors';
import { FilterList } from '@material-ui/icons';
import TextStyles, { fontFamilies } from '../../../theme/text';
import Scrollbars from 'react-custom-scrollbars';
import { SecondaryButton, TertiaryButton } from 'kagr-ui';

export interface FilterMenuProps {
  /** state of parent collapsible sidebar */
  isCollapsed?: boolean;
  isLoading?: boolean;
  /** Mark whether inputs are in a valid state to apply */
  isInvalid?: boolean;
  activeFilterCount?: number;
  onCancel: () => void;
  onApply: () => void;
  onClearAll: () => void;
  children: React.ReactNode;
}

const useStyles = makeStyles<Theme, { isCollapsed?: boolean }>({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  header: props => ({
    height: 43,
    padding: 8,
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: fontFamilies.heading,
    borderBottom: props.isCollapsed ? 'none' : `1px solid ${neutral[300]}`
  }),
  iconWrapper: props => ({
    width: props.isCollapsed ? '100%' : 'auto',
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      display: 'block',
      margin: props.isCollapsed ? '0 auto' : '0 5px 0 0'
    },
    '& .filters': {
      margin: 0,
      fontWeight: 600,
      fontSize: '1rem'
    }
  }),
  loadingFilters: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '12px'
  },
  inactive: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '12px',
    ...TextStyles.label1,
    whiteSpace: 'nowrap',
    opacity: 1,
    transition: 'opacity 150ms',
    '&.collapsed': {
      opacity: 0
    }
  },
  filterActions: props => ({
    visibility: props.isCollapsed ? 'hidden' : 'visible',
    borderBottom: `1px solid ${neutral[300]}`,
    padding: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& button:first-of-type': {
      marginRight: 8
    }
  }),
  content: props => ({
    '& > div, & > fieldset': {
      visibility: props.isCollapsed ? 'hidden' : 'visible',
      padding: 8,
      marginTop: 8,
      whiteSpace: 'nowrap',
      width: '100%',
      boxSizing: 'border-box',
      '&:not(:last-child)': {
        borderBottom: `1px solid ${neutral[300]}`
      }
    }
  }),
  clearFilters: {
    display: 'flex',
    alignItems: 'center'
  },
  chip: {
    fontSize: 12,
    lineHeight: '14px',
    fontFamily: fontFamilies.body,
    color: 'white',
    backgroundColor: blue[500],
    padding: '0 8px',
    borderRadius: '8px'
  }
});

/**
 * Render filter sidebar content
 */
const FilterMenu = ({
  isCollapsed,
  isLoading,
  isInvalid,
  activeFilterCount,
  onApply,
  onCancel,
  onClearAll,
  children
}: FilterMenuProps) => {
  const classes = useStyles({ isCollapsed });

  return (
    <form
      className={classes.root}
      onSubmit={event => {
        onApply();
        event.preventDefault();
      }}
      onReset={event => {
        onClearAll();
        event.preventDefault();
      }}
    >
      <div className={classes.header}>
        <div className={classes.iconWrapper}>
          <FilterList />
          {!isCollapsed && <p className="filters">Filters</p>}
        </div>
        {!isCollapsed && activeFilterCount > 0 && (
          <div className={classes.clearFilters}>
            <TertiaryButton
              component="button"
              title="Clear All"
              text="Clear All"
              handleClick={onClearAll}
              className={classes.clearButton}
            />
            <span className={classes.chip}>{activeFilterCount}</span>
          </div>
        )}
      </div>
      <div className={classes.filterActions}>
        <TertiaryButton component="button" text="cancel" title="cancel" handleClick={onCancel} />
        <SecondaryButton text="Apply" title="Apply" handleClick={onApply} deactive={isLoading || isInvalid} />
      </div>
      <Scrollbars style={{ flex: 1 }} hidden={isCollapsed}>
        <div className={classes.content}>{children}</div>
      </Scrollbars>
    </form>
  );
};

export default FilterMenu;
